import { useMemo } from 'react';
import { useSimulationStore } from '@/state/simulationStore';
import { useBrainStore } from '@/state/brainStore';
import { useStudioScopeIds } from '@/state/useActiveStudio';
import { useOpsView } from '@/api/views';
import { buildOpsWorklist } from '@/domain/opsWorklist';
import { opsUnpaidArtists } from '@/domain/opsUnpaidArtists';

/**
 * 7kta: the ONE Ops worklist derivation, shared by the Ops screen and the left
 * rail's Ops badge, so the badge always equals the page's count. Reads live via
 * /api/views/ops (store fallback when unreachable), scoped by the top-bar
 * switcher (archived studios out on "All", branding-3).
 *
 * `enabled: false` (any persona but the Ops seat, for the rail) skips the view
 * fetch and returns an empty worklist.
 */
export function useOpsWorklist({ enabled = true }: { enabled?: boolean } = {}) {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const scopeIds = useStudioScopeIds();
  // Anas 8 Oct: names the bookings agent whose "Client messaged" is overdue.
  const agents = useBrainStore((s) => s.agents);
  // Called UNSCOPED: buildOpsWorklist re-applies the studio scope item by item
  // (incl. the "booking went missing, surface the override anyway" rule).
  const { data: opsBundle } = useOpsView(undefined, { enabled });
  const bundle = enabled ? opsBundle : undefined;

  const unpaidArtists = useMemo(
    () =>
      opsUnpaidArtists({
        nowIso: clockIso,
        studioIds: scopeIds,
        bookings: bundle?.bookings ?? {},
        artists: bundle?.artists ?? {},
        payoutRecords: bundle?.payoutRecords ?? {},
        salePayments: bundle?.salePayments ?? {},
      }),
    [clockIso, scopeIds, bundle],
  );

  const worklist = useMemo(
    () =>
      buildOpsWorklist({
        nowIso: clockIso,
        studioIds: scopeIds,
        bookings: bundle?.bookings ?? {},
        overrideRequests: bundle?.overrideRequests ?? {},
        topupPlans: bundle?.topupPlans ?? {},
        noRebookFlags: bundle?.noRebookFlags ?? {},
        payoutRecords: bundle?.payoutRecords ?? {},
        unpaidArtists,
        artists: bundle?.artists ?? {},
        customers: bundle?.customers ?? {},
        deposits: bundle?.deposits ?? {},
        paybyExtensionRequests: bundle?.paybyExtensionRequests ?? {},
        pushedCustomerIds: bundle?.pushedCustomerIds,
        customerBookings: bundle?.customerBookings,
        agents,
      }),
    [clockIso, scopeIds, bundle, unpaidArtists, agents],
  );

  return { worklist, opsBundle: bundle, scopeIds, unpaidArtists };
}
